import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
    AuthorityRegisterSummaryDto,
    FlyFreelyError,
    FlyFreelyLoggingService,
    OrganisationAuthorityDto,
    OrganisationAuthorityGroup,
    OrganisationAuthorityService,
    PersonsOrganisationDto,
    WorkTracker,
    toLocalDate
} from '@flyfreely-portal-ui/flyfreely';
import { FormatDatePipe } from '@flyfreely-portal-ui/ui';
import { WorkspaceStateService } from '@flyfreely-portal-ui/workspace';
import { isDiscontinued } from 'libs/authorities/src/lib/helpers';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

interface AuthorityOption {
    id: number;
    name: string;
    registers: AuthorityRegisterSummaryDto[];
}

interface RegisterOption {
    id: number;
    name: string;
}

@Component({
    selector: 'personnel-add-to-register-dialogue',
    templateUrl: './personnel-add-to-register-dialogue.component.html',
    styleUrls: ['./personnel-add-to-register-dialogue.component.scss']
})
export class PersonnelAddToRegisterDialogueComponent
    implements OnInit, OnDestroy
{
    @Input() personId: number;
    @Input() managingOrganisationId: number;

    organisation: PersonsOrganisationDto;

    addToRegisterFormGroup: FormGroup;
    authorities: AuthorityOption[] = [];
    availableRegisters: RegisterOption[] = [];

    loadingAuthorities = false;
    loadingRegisters = false;
    statuses = [
        { name: 'Pending', value: 'PENDING' },
        { name: 'Active', value: 'ACTIVE' },
        { name: 'Suspended', value: 'SUSPENDED' },
        { name: 'Auto', value: 'AUTO' }
    ];
    working = false;

    private workTracker = new WorkTracker();
    private ngUnsubscribe$ = new Subject<void>();

    constructor(
        private organisationAuthorityService: OrganisationAuthorityService,
        private logging: FlyFreelyLoggingService,
        public modal: BsModalRef<PersonnelAddToRegisterDialogueComponent>,
        private workspaceStateService: WorkspaceStateService,
        private formatDatePipe: FormatDatePipe
    ) {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));
    }

    ngOnInit(): void {
        this.workspaceStateService.currentLoadedOrganisation$
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(organisation => {
                this.organisation = organisation.organisation;
                this.loadAuthorities();
            });
        this.initForm();
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    private initForm() {
        this.addToRegisterFormGroup = new FormGroup({
            authorityId: new FormControl(null, [Validators.required]),
            registerId: new FormControl(null, [Validators.required]),
            startDate: new FormControl(null, [Validators.required]),
            endDate: new FormControl(null),
            statusSource: new FormControl('AUTO', [Validators.required])
        });
    }

    private loadAuthorities() {
        this.loadingAuthorities = true;

        this.organisationAuthorityService
            .findAuthorities(this.organisation.id, this.managingOrganisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: authorityGroups => {
                    this.authorities = authorityGroups
                        .filter(
                            group =>
                                !isDiscontinued(group) &&
                                group.authorities &&
                                group.authorities.length > 0
                        )
                        .reduce((acc: AuthorityOption[], group) => {
                            const authoritiesWithRegisters = group.authorities
                                .filter(
                                    auth =>
                                        auth.expired === false &&
                                        auth.archived === false &&
                                        auth.registers &&
                                        auth.registers.length > 0
                                )
                                .filter(auth =>
                                    auth.registers.some(
                                        register =>
                                            register.registerEntityType ===
                                                AuthorityRegisterSummaryDto
                                                    .RegisterEntityType.PILOT &&
                                            register.registerMode !==
                                                AuthorityRegisterSummaryDto
                                                    .RegisterMode.DISABLED
                                    )
                                )
                                .map(auth => ({
                                    id: auth.id,
                                    name: this.formatAuthorityName(group, auth),
                                    registers: auth.registers.filter(
                                        register =>
                                            register.registerEntityType ===
                                                AuthorityRegisterSummaryDto
                                                    .RegisterEntityType.PILOT &&
                                            register.registerMode !==
                                                AuthorityRegisterSummaryDto
                                                    .RegisterMode.DISABLED
                                    )
                                }));
                            return acc.concat(authoritiesWithRegisters);
                        }, []);

                    this.loadingAuthorities = false;
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error loading authorities: ${error.message}`
                    );
                    this.loadingAuthorities = false;
                }
            })
            .add(this.workTracker.createTracker());
    }

    private formatAuthorityName(
        group: OrganisationAuthorityGroup,
        authority: OrganisationAuthorityDto
    ): string {
        // Format similar to FormatAuthorityPipe but without organization name
        const authorityName =
            group.abbreviation != null ? group.abbreviation : group.name;

        const identifier =
            authority.identifier != null && group.hasIdentifier
                ? `${group.identifierLabel} ${authority.identifier}`
                : '';

        const dateRange =
            this.formatDatePipe.transform(authority.startDate) +
            (authority.expiryDate != null
                ? ` - ${this.formatDatePipe.transform(authority.expiryDate)}`
                : '');

        return `${authorityName} ${identifier} (${dateRange})`.trim();
    }

    onAuthorityChange(selectedAuthority: AuthorityOption) {
        this.addToRegisterFormGroup.get('registerId').setValue(null);
        this.availableRegisters = [];

        if (selectedAuthority) {
            this.availableRegisters = selectedAuthority.registers.map(
                register => ({
                    id: register.id,
                    name: register.name
                })
            );
        }
    }

    onSave() {
        if (this.addToRegisterFormGroup.invalid) {
            this.addToRegisterFormGroup.markAllAsTouched();
            this.logging.error(null, 'Please fill in all required fields');
            return;
        }

        const formValue = this.addToRegisterFormGroup.value;

        const selectedAuthority = this.authorities.find(
            auth => auth.id === formValue.authorityId
        );
        const selectedRegister = this.availableRegisters.find(
            reg => reg.id === formValue.registerId
        );

        if (!selectedAuthority || !selectedRegister) {
            this.logging.error(null, 'Invalid authority or register selection');
            return;
        }

        this.organisationAuthorityService
            .personnelRegister(formValue.authorityId, formValue.registerId)
            .createRegisterEntry({
                personId: this.personId,
                startDate: toLocalDate(formValue.startDate),
                endDate: formValue.endDate
                    ? toLocalDate(formValue.endDate)
                    : null,
                statusSource: formValue.statusSource
            })
            .pipe(
                tap(() =>
                    this.organisationAuthorityService.notifyUpdated(
                        formValue.authorityId
                    )
                ),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe({
                next: () => {
                    this.logging.success(
                        `Successfully added personnel to ${selectedAuthority.name} - ${selectedRegister.name} register`
                    );
                    this.modal.hide();
                },
                error: (error: FlyFreelyError) => {
                    this.logging.error(
                        error,
                        `Error adding personnel to register: ${error.message}`
                    );
                }
            })
            .add(this.workTracker.createTracker());
    }
}
